import { FunctionComponent, useContext, useEffect, useState } from "react";
import backgroundImage from "assets/images/jpg/SignUpPageMask.jpg";
import LogoImage from "assets/images/png/SignInLogo.png";
import { useForm } from "react-hook-form";
import {
  useCreateBrokerMutation,
  useGetAccountQuery,
  useGetCurrentBrokerQuery,
  useGetUsStatesQuery,
  useSendEmailVerificationCodeMutation,
} from "gql/generated.graphql";
import { getList as getCountries } from "country-list-with-dial-code-and-flag";
import { ViewerContext, localStorage } from "dash-ui";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import {
  GTMEventsEnum,
  GTMSAuthEnum,
} from "common/google-tag-manager/constants";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ImSpinner8 } from "react-icons/im";
import { RoutesPath } from "flow-components/routes/routes";

// Schema for form data
const registerFormSchema = z
  .object({
    first_name: z
      .string()
      .min(1, "First name is required.")
      .min(2, "First name must have at least 2 characters.")
      .max(20, "First name must have at most 20 characters.")
      .regex(
        new RegExp(/^[a-zA-Z\u00C0-\u017F-]+$/),
        "First name must contains only letters and hyphen."
      )
      .regex(
        new RegExp(/^(?!-).*(?<!-)$/),
        "First name cannot begin or end with a hyphen."
      ),
    last_name: z
      .string()
      .min(1, "Last name is required.")
      .min(2, "Last name must have at least 2 characters.")
      .max(20, "Last name must have at most 20 characters.")
      .regex(
        new RegExp(/^[a-zA-Z\u00C0-\u017F-]+$/),
        "Last name must contains only letters and hyphen."
      )
      .regex(
        new RegExp(/^(?!-).*(?<!-)$/),
        "Last name cannot begin or end with a hyphen."
      ),
    email: z
      .string()
      .min(1, "Email is required.")
      .email("Email must be valid."),
    phone_number: z
      .string()
      .min(1, "Phone number is required.")
      .length(10, "Phone number must have 10 numbers.")
      .regex(
        new RegExp(/^[0-9]+$/),
        "Phone number must contains only numbers."
      ),
    country: z.string({ required_error: "Country is required." }),
    state: z.string().optional(),
    agent_license_number: z
      .string()
      .max(20, "License number must have at most 20 characters.")
      .optional(),
    password: z
      .string()
      .min(1, "Password is required.")
      .min(6, "Password must have at least 6 characters.")
      .regex(
        new RegExp(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).*$/),
        "Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number and a special character."
      ),
    confirm_password: z.string().min(1, "Password confirmation is required."),
    agreement: z.boolean().refine((value) => value === true, {
      message: "You must accept the terms and conditions.",
    }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Password confirmation does not match.",
    path: ["confirm_password"],
  })
  .refine(
    (data) => {
      if (data.country === "US" && !data.state) {
        return false;
      }
      return true;
    },
    {
      message: "State is required.",
      path: ["state"],
    }
  )
  .refine(
    (data) => {
      if (data.country === "US" && !data.agent_license_number) {
        return false;
      }
      return true;
    },
    {
      message: "License number is required.",
      path: ["agent_license_number"],
    }
  );

// Main Component
export const SignUpPage: FunctionComponent = () => {
  // Type for form data
  type FormData = z.infer<typeof registerFormSchema>;

  // React states
  const [loading, setLoading] = useState<boolean>(false);

  // Hooks
  const signUpForm = useForm<FormData>({
    defaultValues: { country: "US" },
    resolver: zodResolver(registerFormSchema),
  });

  const countries = getCountries();
  const auth = useContext(ViewerContext);
  const navigate = useNavigate();

  // Queries and mutations
  const getUsStates = useGetUsStatesQuery();
  const [sendEmailVerificationCodeMutation] =
    useSendEmailVerificationCodeMutation();
  const [createBrokerMutation] = useCreateBrokerMutation();
  const getAccountQuery = useGetAccountQuery({
    skip: !auth.tokens,
    fetchPolicy: "no-cache",
  });
  const getCurrentBrokerQuery = useGetCurrentBrokerQuery();

  // Derived states
  const shouldRenderUSInfo = signUpForm.watch("country") === "US";
  const selectedCountry = countries.find(
    (country) => country.code === signUpForm.watch("country")
  );

  // Handle submit registration
  const onSubmit = async (formData: FormData) => {
    setLoading(true);
    // Get dial_code for create broker mutation
    const dial_code =
      countries.find((country) => country.code === formData.country)
        ?.dial_code || "";
    // Format first name and last name to avoid inconsistent data
    const formattedFirstName =
      formData.first_name.charAt(0).toUpperCase() +
      formData.first_name.slice(1);
    const formattedLastName =
      formData.last_name.charAt(0).toUpperCase() + formData.last_name.slice(1);
    // Register user (account, profile and broker) on API
    try {
      await createBrokerMutation({
        variables: {
          input: {
            dial_code,
            email: formData.email,
            first_name: formattedFirstName,
            last_name: formattedLastName,
            password: formData.password,
            phone_number: formData.phone_number,
            license_number: formData.agent_license_number,
            state_code: formData.state,
          },
        },
        onCompleted: async (createBrokerData) => {
          // Save authentication data on local storage to use update mutation
          localStorage.setAccessToken(
            createBrokerData.createBroker.accessToken
          );
          localStorage.setRefreshToken(
            createBrokerData.createBroker.refreshToken
          );
          localStorage.setRefreshTokenExpiry(
            createBrokerData.createBroker.refreshTokenExpiry || ""
          );

          // Create Google Tag Manager Event
          createGTMEvent({
            dataLayer: {
              event: GTMEventsEnum.SIGN_UP,
              user_id: getGoogleTagManagerUserId(formData.email),
              method: GTMSAuthEnum.EMAIL,
            },
          });

          // Send email with confirmation code to user
          try {
            await sendEmailVerificationCodeMutation({
              onError: (error) => {
                throw new Error(error.message);
              },
            });
          } catch (error) {
            console.error(error);
            setLoading(false);
            return;
          }

          // Authenticate created user
          const getLoggedAccountQuery = await getAccountQuery.refetch();
          const getCurrentBroker = await getCurrentBrokerQuery.refetch();

          if (auth.setAccount && auth.setProfile && auth.setBroker) {
            auth.setAccount(getLoggedAccountQuery.data.getAccount);
            auth.setBroker(getCurrentBroker.data.getCurrentBroker);
            auth.setProfile(getCurrentBroker.data?.getCurrentBroker.profile);
          }

          // Redirect authenticated user
          navigate("/auth/complete-sign-up");

          return;
        },
        onError: (error) => {
          if (error.message === "ACCOUNT_EMAIL_ALREADY_EXIST") {
            signUpForm.setError("email", {
              message: "Email already registered.",
            });
          }
          if (error.message === "PHONE_NUMBER_IS_USED") {
            signUpForm.setError("phone_number", {
              message: "Phone number already registered.",
            });
          }
          if (error.message === "LICENSE_ALREADY_EXISTS") {
            signUpForm.setError("agent_license_number", {
              message:
                "Agent license number already registered. Please contact support.",
            });
          }
          throw new Error(error.message);
        },
      });
    } catch (error) {
      console.error(error);
      setLoading(false);
      return;
    }
  };

  // redirect to home page effect
  useEffect(() => {
    if (auth.account) {
      // Redirect if has redirect param
      navigate(RoutesPath.Dashboard);
    }
  }, [auth.account, navigate]);

  // HTML component
  return (
    <div className="max-w-screen w-full min-h-screen flex flex-col bg-slate-50">
      <main className="w-full flex flex-1">
        <div className="flex w-0 lg:w-5/12 bg-sky-600 items-center justify-center relative">
          <img
            src={backgroundImage}
            alt="People at Dash"
            className="absolute h-full w-full object-cover"
          />
          <img src={LogoImage} alt="Dash Logo" className="z-10" />
        </div>

        <div className="flex w-full lg:w-7/12 justify-center max-h-full overflow-auto my-auto">
          <div className="flex flex-col gap-8 w-full max-w-lg text-slate-900 p-4">
            <div className="flex flex-col gap-2">
              <h1 className="text-3xl font-[Montserrat]">
                Create a Dash Account
              </h1>
              <p className="text-slate-500">
                Your privacy is important to us. DASH will never share your
                information.
              </p>
            </div>

            <form
              id="signup-form"
              className="signup-form flex flex-col gap-4"
              onSubmit={signUpForm.handleSubmit(onSubmit)}
            >
              <div className="flex gap-4 flex-col md:flex-row">
                <div className="flex flex-col flex-1">
                  <label className="text-xs text-slate-500">First Name</label>
                  <input
                    {...signUpForm.register("first_name")}
                    placeholder="Brooklyn"
                    type="text"
                    className={`w-full flex items-center h-10 px-3 rounded border ${
                      signUpForm.formState.errors.first_name
                        ? "border-red-600"
                        : "border-slate-400"
                    }`}
                  />
                  <span className="text-sm text-red-600">
                    {signUpForm.formState.errors.first_name?.message}
                  </span>
                </div>

                <div className="flex flex-col flex-1">
                  <label className="text-xs text-slate-500">Last Name</label>
                  <input
                    {...signUpForm.register("last_name")}
                    placeholder="Simmonsy"
                    type="text"
                    className={`w-full flex items-center h-10 px-3 rounded border ${
                      signUpForm.formState.errors.last_name
                        ? "border-red-600"
                        : "border-slate-400"
                    }`}
                  />
                  <span className="text-sm text-red-600">
                    {signUpForm.formState.errors.last_name?.message}
                  </span>
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-xs text-slate-500">Email</label>
                <input
                  {...signUpForm.register("email")}
                  type="email"
                  placeholder="john@dash.com"
                  className={`flex items-center h-10 px-3 rounded border  ${
                    signUpForm.formState.errors.email
                      ? "border-red-600"
                      : "border-slate-400"
                  }`}
                />
                <span className="text-sm text-red-600">
                  {signUpForm.formState.errors.email?.message}
                </span>
              </div>

              <div className="flex flex-col">
                <label className="text-xs text-slate-500">Country</label>
                <select
                  className="flex items-center h-10 px-3 rounded border border-slate-400 invalid:text-slate-400"
                  {...signUpForm.register("country")}
                >
                  <option disabled hidden>
                    Select your country
                  </option>

                  {countries.map((country) => {
                    return (
                      <option key={country.name} value={country?.code}>
                        {country?.name}
                      </option>
                    );
                  })}
                </select>
                <span className="text-sm text-red-600">
                  {signUpForm.formState.errors.country?.message}
                </span>
              </div>

              <div className="flex flex-col">
                <label className="text-xs text-slate-500">Phone Number</label>
                <div
                  className={`flex h-10 pl-3 items-center gap-3 w-full rounded border ${
                    signUpForm.formState.errors.phone_number
                      ? "border-red-600"
                      : "border-slate-400"
                  } has-[:focus]:outline has-[:focus]:outline-1 bg-white`}
                >
                  <p className="min-w-fit">{selectedCountry?.dial_code}</p>
                  <input
                    {...signUpForm.register("phone_number")}
                    placeholder="123123123"
                    type="tel"
                    maxLength={10}
                    className="w-full rounded bg-transparent outline-none"
                  />
                </div>
                <span className="text-red-600 text-sm">
                  {signUpForm.formState.errors.phone_number?.message}
                </span>
              </div>

              {shouldRenderUSInfo && (
                <div className="flex gap-4 flex-col md:flex-row">
                  <div className="flex flex-col flex-1">
                    <label className="text-xs text-slate-500">State</label>
                    <select
                      className={`h-10 flex items-center px-3 rounded border invalid:text-slate-400 ${
                        signUpForm.formState.errors.state
                          ? "border-red-600"
                          : "border-slate-400"
                      }`}
                      defaultValue={""}
                      {...signUpForm.register("state")}
                    >
                      <option value="" disabled hidden>
                        Select your state
                      </option>

                      {getUsStates.data?.getUSStates.map((state) => {
                        return (
                          <option key={state._id} value={state?.code}>
                            {state?.name}
                          </option>
                        );
                      })}
                    </select>
                    <span className="text-sm text-red-600">
                      {signUpForm.formState.errors.state?.message}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1">
                    <label className="text-xs text-slate-500">
                      Agent License Number
                    </label>
                    <input
                      placeholder="123456789"
                      type="text"
                      maxLength={10}
                      className={`h-10 flex items-center px-3 rounded border ${
                        signUpForm.formState.errors.agent_license_number
                          ? "border-red-600"
                          : "border-slate-400"
                      }`}
                      {...signUpForm.register("agent_license_number")}
                    />
                    <span className="text-red-600 text-sm">
                      {
                        signUpForm.formState.errors.agent_license_number
                          ?.message
                      }
                    </span>
                  </div>
                </div>
              )}

              <div className="flex flex-col">
                <label className="text-xs text-slate-500">Password</label>
                <input
                  {...signUpForm.register("password")}
                  placeholder="Enter password"
                  type="password"
                  className={`h-10 flex items-center px-3 rounded border  ${
                    signUpForm.formState.errors.password
                      ? "border-red-600"
                      : "border-slate-400"
                  }`}
                />
                <span className="text-sm text-red-600">
                  {signUpForm.formState.errors.password?.message}
                </span>
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-slate-500">
                  Confirm Password
                </label>
                <input
                  {...signUpForm.register("confirm_password")}
                  placeholder="Confirm password"
                  type="password"
                  className={`h-10 flex items-center px-3 rounded border  ${
                    signUpForm.formState.errors.confirm_password
                      ? "border-red-600"
                      : "border-slate-400"
                  }`}
                />
                <span className="text-sm text-red-600">
                  {signUpForm.formState.errors.confirm_password?.message}
                </span>
              </div>

              <div className="flex flex-col items-start mt-4">
                <div className="flex gap-2 items-start">
                  <input
                    {...signUpForm.register("agreement")}
                    type="checkbox"
                    name="agreement"
                    className="flex h-5 w-5 mt-1"
                  />
                  <label className="flex-1" htmlFor="agreement">
                    I agree to the{" "}
                    <Link to={RoutesPath.TermConditions} target="_blank">
                      terms and conditions
                    </Link>{" "}
                    and{" "}
                    <Link to={RoutesPath.PrivacyPolicy} target="_blank">
                      privacy policy
                    </Link>
                    .
                  </label>
                </div>
                <span className="text-sm text-red-600">
                  {signUpForm.formState.errors.agreement?.message}
                </span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex h-10 items-center justify-center bg-sky-600 px-3 rounded text-white mt-4 disabled:opacity-30"
              >
                {loading ? (
                  <ImSpinner8 className="animate-spin" />
                ) : (
                  "Create account"
                )}
              </button>
            </form>

            <p className="pb-4">
              Already a DASH member?
              <a href="/auth/sign-in"> Log in</a>
            </p>
          </div>
        </div>
      </main>

      <footer className="flex flex-col md:flex-row items-center justify-center h-auto lg:h-12 py-10 lg:py-0 gap-4 lg:gap-10 text-sm bg-slate-900">
        <Link
          to={RoutesPath.TermConditions}
          className="text-white underline underline-offset-4"
        >
          Terms & Conditions
        </Link>
        <Link
          to={RoutesPath.PrivacyPolicy}
          className="text-white underline underline-offset-4"
        >
          Privacy Policy
        </Link>
        <span className="text-white">2025 DASH Inc. All Rights Reserved.</span>
      </footer>
    </div>
  );
};
